import "./assets/scss/app.scss";
import "./assets/scss/icons.scss";
import "bootstrap/js/dist/scrollspy";
import "react-image-lightbox/style.css";

import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Route,
  Navigate,
} from "react-router-dom";

// Layout
import { LayoutType } from "constants/types";

// redux
import { RootState } from "redux/store";
import { useSelector } from "react-redux";

// layouts
import FrontLayout from "elements/Layer/FrontLayout";
import InnerVerticalLayout from "elements/Layer/InnerVerticalLayout";
import InnerHorizontalLayout from "elements/Layer/InnerHorizontalLayout";
import InnerMobileLayout from "elements/Layer/InnerMobileLayout";

// front
import SignIn from "pages/auth/Login";
import LogOut from "pages/auth/Logout";
import ForgotPassword from "pages/auth/ForgotPassword";
import ResetPassword from "pages/auth/ResetPassword";
import OtpVerification from "pages/auth/OtpVerification";
import VerifyEmail from "pages/auth/VerifyEmail";

// app
import AppStarter from "pages/app/Starter";
import AppHome from "pages/app/Home";

// general
import Error from "pages/general/Error404";
import SuccessReset from "pages/auth/SucessReset";
import SuccessVerification from "pages/auth/SuccessVerification";
import NotificationPreferences from "pages/notification-preference/list";
import Users from "pages/users/list";
import UserForm from "pages/users/form";
import UserDeletionForm from "pages/users/userDeletionForm";
import Localities from "pages/localities/list";
import LocalityForm from "pages/localities/form";
import SubLocalities from "pages/sub-localities/list";
import SubLocalityForm from "pages/sub-localities/form";
import Incidents from "pages/incidents/list";
import IncidentForm from "pages/incidents/form";
import IncidentAdminForm from "pages/incidents/adminForm";
import POI from "pages/point-of-interest/list";
import POIForm from "pages/point-of-interest/form";
import Feeds from "pages/scanner-feeds/list";
import FeedForm from "pages/scanner-feeds/form";
import Links from "pages/links/list";
import LinkForm from "pages/links/form";
import Advertisements from "pages/advertisements/list";
import AdvertisementForm from "pages/advertisements/form";
import Analytics from "pages/analytics";
import UnitList from "pages/units/list";
import AddUnit from "pages/units/form";
import TitleList from "pages/titles/list";
import AddTitle from "pages/titles/form";


function App() {
  const { Layout } = useSelector((state: RootState) => ({
    Layout: state.Layout,
  }));

  const getLayout = () => {
    let layoutCls = InnerVerticalLayout;
    if (Layout.layoutType === LayoutType.LAYOUT_HORIZONTAL) {
      layoutCls = InnerHorizontalLayout;
    }
    return layoutCls;
  };

  // Mobile Theme Layout
  const getMobileLayout = () => {
    let layoutCls1 = InnerMobileLayout;
    if (Layout.layoutType === LayoutType.LAYOUT_HORIZONTAL) {
      layoutCls1 = InnerMobileLayout;
    }
    return layoutCls1;
  };

  let RouterLayout = getLayout();
  let RouterMobileLayout = getMobileLayout();
  // Configure nested routes with JSX
  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route path="/">
        <Route path="*" element={<Error />}></Route>
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/" element={<FrontLayout />}>
          <Route path="login" element={<SignIn />} />
          <Route path="password/forgot" element={<ForgotPassword />} />
          <Route path="password/reset" element={<ResetPassword />} />
          <Route path="otp/verification" element={<OtpVerification />} />
          {/* Signup email verification. Distinct from otp/verification above,
              which is the forgot-password OTP and ends at a password reset. */}
          <Route path="verify/email" element={<VerifyEmail />} />
          <Route path="password/success" element={<SuccessReset />} />
          {/* Self-registration removed 2026-08-13. It could not produce a
              usable portal account: the API hard-codes self-signup to
              basic_user as an anti-privilege-escalation guard, and the login
              screen admits only admin and super — so the best case was an
              account that bounced at sign-in. Admins are created by a super on
              the Users page. Redirects rather than 404s, for anyone holding an
              old link. */}
          <Route path="register" element={<Navigate to="/login" replace />} />
          <Route path="logout" element={<LogOut />} />
          <Route path="confirm/success" element={<SuccessVerification />} />
        </Route>
        {/* <Route path="noauth/create/incident" element={<IncidentAdminForm />} /> */}
        <Route path="noauth/" element={<RouterMobileLayout />}>
          <Route path="create/incident" element={<IncidentAdminForm />} />
        </Route>
        <Route path="/app" element={<RouterLayout />}>
          <Route path="home" element={<AppHome />} />
          <Route path="starter" element={<AppStarter />} />
        </Route>
        <Route path="user/" element={<RouterLayout />}>
          <Route path="list" element={<Users />} />
          <Route path="form" element={<UserForm />} />
        </Route>
        <Route path="preference/" element={<RouterLayout />}>
          <Route path="list" element={<NotificationPreferences />} />
        </Route>
        <Route path="locality/" element={<RouterLayout />}>
          <Route path="list" element={<Localities />} />
          <Route path="form" element={<LocalityForm />} />
        </Route>

        <Route path="sublocality/" element={<RouterLayout />}>
          <Route path="list" element={<SubLocalities />} />
          <Route path="form" element={<SubLocalityForm />} />
        </Route>

        <Route path="unit/" element={<RouterLayout />}>
          <Route path="list" element={<UnitList />} />
          <Route path="form" element={<AddUnit />} />
        </Route>

        <Route path="title/" element={<RouterLayout />}>
          <Route path="list" element={<TitleList />} />
          <Route path="form" element={<AddTitle />} />
        </Route>

        <Route path="incident/" element={<RouterLayout />}>
          <Route path="list" element={<Incidents />} />
          <Route path="form" element={<IncidentForm />} />
        </Route>

        <Route path="poi/" element={<RouterLayout />}>
          <Route path="list" element={<POI />} />
          <Route path="form" element={<POIForm />} />
        </Route>

        <Route path="feed/" element={<RouterLayout />}>
          <Route path="list" element={<Feeds />} />
          <Route path="form" element={<FeedForm />} />
        </Route>

        <Route path="link/" element={<RouterLayout />}>
          <Route path="list" element={<Links />} />
          <Route path="form" element={<LinkForm />} />
        </Route>
        <Route path="advertisement/" element={<RouterLayout />}>
          <Route path="list" element={<Advertisements />} />
          <Route path="form" element={<AdvertisementForm />} />
        </Route>

        <Route path="/" element={<RouterLayout />}>
          <Route path="analytics" element={<Analytics />} />
        </Route>
        <Route path="account/" element={<RouterMobileLayout />}>
          <Route path="deletion" element={<UserDeletionForm />} />
        </Route>
      </Route>
    )
  );
  return <RouterProvider router={router} />;
}

export default App;
